import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import i18n from "@/i18n";
import { callApi } from "../services/api.service";
import { ExamConfig, ExamConfigUpdate } from "../types/exam-config.types";

const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;

// Query keys
export const examConfigKeys = {
  all: ['examConfig'] as const,
  detail: (jobId: string) => [...examConfigKeys.all, jobId] as const,
};

// Hook to fetch exam config for a job
export const useExamConfig = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<ExamConfig, Error>({
    queryKey: examConfigKeys.detail(jobId),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await callApi<ExamConfig>(
        `${apiServerUrl}/api/jobs/offers/${jobId}/exam-config`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data as ExamConfig;
    },
    enabled: !!jobId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hook to update exam config for a job
export const useUpdateExamConfig = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (configUpdate: ExamConfigUpdate) => {
      const accessToken = await getAccessTokenSilently();
      const response = await callApi<ExamConfig>(
        `${apiServerUrl}/api/jobs/offers/${jobId}/exam-config`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(configUpdate),
        }
      );
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data as ExamConfig;
    },

    onMutate: async (newConfig) => {
      await queryClient.cancelQueries({ queryKey: examConfigKeys.detail(jobId) });
      const previous = queryClient.getQueryData(examConfigKeys.detail(jobId));

      queryClient.setQueryData(examConfigKeys.detail(jobId), (old: any) => ({
        ...old,
        language: newConfig.language,
        config_data: newConfig.config_data,
        ...(newConfig.interview_expiry_days !== undefined
          ? { interview_expiry_days: newConfig.interview_expiry_days }
          : {}),
        version: (old?.version || 0) + 1,
      }));

      return { previous };
    },

    onError: (_err, _newConfig, context) => {
      queryClient.setQueryData(examConfigKeys.detail(jobId), context?.previous);
      toast.error(i18n.t('common:toasts.save_failed_reverted'));
    },

    onSuccess: () => {
      toast.success(i18n.t('common:toasts.saved'));
    }
  });
};
